import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';

export const TRENDS_PAGE_SIZE = 10;

export type TrendsCategory = 'trendingUp' | 'anomalies';

/**
 * Page-based data for one Trends section. Calls insights.trendingUp or
 * insights.anomalies with offset derived from the (1-based) page. Both queries
 * are mounted with `enabled` gating (one is always disabled) rather than a
 * single useTRPCQuery over a union of the two option types, which breaks tRPC's
 * overload resolution.
 *
 * `limit` is how many rows one page holds. The Trends screen takes the default
 * ten; the Home panes ask for the shorter lists the design shows there. The
 * offset follows the same limit, so paging stays consistent either way.
 */
export function useTrendsSection(
  category: TrendsCategory,
  entityType: 'artist' | 'track',
  page: number,
  rangeDays: number,
  date?: string,
  limit: number = TRENDS_PAGE_SIZE
) {
  const trpcMusicAPI = useTRPCAPI();
  const offset = Math.max(0, (page - 1) * limit);
  const isTrending = category === 'trendingUp';
  const dateInput = date !== undefined ? { date } : {};
  // The anomalies rolling window caps at 30 days server-side.
  const anomaliesDays = Math.min(Math.max(rangeDays, 1), 30);

  const trendingQuery = useTRPCQuery(
    trpcMusicAPI.insights.trendingUp.queryOptions({ entityType, limit, offset, ...dateInput }, { enabled: isTrending })
  );
  const anomaliesQuery = useTRPCQuery(
    trpcMusicAPI.insights.anomalies.queryOptions(
      { entityType, days: anomaliesDays, limit, offset, ...dateInput },
      { enabled: !isTrending }
    )
  );

  const active = isTrending ? trendingQuery : anomaliesQuery;
  return {
    rows: active.data?.rows ?? [],
    hasMore: active.data?.hasMore ?? false,
    isLoading: active.isLoading,
    error: active.error,
    refetch: active.refetch,
    offset,
  };
}
